// Web-exclusive Profile screen for Facilities (isDoctor === false)
// Matches design from ref/facilities-pages.html Account page

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Image,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  User,
  Tag,
  Lock,
  Shield,
  HelpCircle,
  Pencil,
  AlertTriangle,
  Globe,
  BookOpen,
  Mail,
  GraduationCap,
  MessageSquare,
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  Search,
  X,
  Check,
  ChevronRight,
} from 'lucide-react';
import WebLayout from '../../components/web/WebLayout';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import {
  getProfile,
  updateProfile,
  getClinics,
  updateClinic,
  deleteUser,
  getOverview,
  postClinicFacilities,
} from '../../services/profile';
import { submitChangePassword, submitFeedback } from '../../services/shared';
import { logout } from '../../services/auth';
import { setLoggedOut } from '../../reducers/auth/user';
import { resetFields } from '../../reducers/profile/profile';
import { getAdministrativeDivisionLabel, getCountryData } from '../../constants/countries';
import { useProfileImageUpload } from '../../hooks/useProfileImageUpload';
import { PLACEHOLDER_PROFILE_URL } from '../../constants/placeholderImage';
import { useFacilityData } from '../../hooks/useFacilityData';
import { extractFacilityTitleAndSubtitle } from '../../utils/facilityUtils';
import { useIsMobile } from '../../hooks/useIsMobile';

// Error keys that have a visible input in each section. Anything the API rejects
// outside these lists has nowhere to render, so it gets surfaced as a toast.
const MANAGER_ERROR_FIELDS = [
  'first_name',
  'phone_number',
  'emergency_name',
  'emergency_number',
];
const FACILITY_ERROR_FIELDS = [
  'name',
  'department',
  'address',
  'city',
  'state',
  'postcode',
  'phone_number',
  'business_registration_number',
];

// Collects messages for error keys that no field in `visibleFields` renders.
const collectHiddenErrors = (errorData, visibleFields) =>
  Object.entries(errorData)
    .filter(([field]) => !visibleFields.includes(field))
    .flatMap(([, messages]) => (Array.isArray(messages) ? messages : [messages]));

const ProfileFacilitiesScreen = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const scrollViewRef = useRef(null);
  const isMobile = useIsMobile();

  // Redux state
  const { slug, country } = useSelector((state) => state.user);

  // Local state
  const [activeSection, setActiveSection] = useState('facility-info');
  const [clinicData, setClinicData] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showFAQ, setShowFAQ] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  // Facility specialization modal state
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [facilitySearchQuery, setFacilitySearchQuery] = useState('');
  const [expandedFacilityTypes, setExpandedFacilityTypes] = useState([]);

  // Data fetching
  const { data: profileQuery, isLoading: profileLoading, refetch: profileRefetch } = useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
  });
  const profileData = profileQuery?.data?.data;

  const { data: clinicsQuery, isLoading: clinicsLoading, refetch: clinicsRefetch } = useQuery({
    queryKey: ['clinics'],
    queryFn: getClinics,
  });

  const { data: overviewQuery, isLoading: overviewLoading } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
  });
  const overviewInfo = overviewQuery?.data?.data;

  // Fetch facility types when modal is open
  const {
    data: facilityTypes,
    isLoading: facilityTypesLoading,
    error: facilityTypesError,
    refetch: refetchFacilityTypes,
  } = useFacilityData(country || 'MY');

  // Mutations
  const updateProfileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries(['profile']);
      setEditingSection(null);
      setErrors({});
      showToast('Manager info updated successfully!', 'success');
    },
    onError: (err) => {
      const errorData = err?.response?.data?.errors || {};
      setErrors(errorData);
      // Field-level errors render inline, but anything without a matching input
      // would otherwise fail silently — surface those in a toast.
      const hidden = collectHiddenErrors(errorData, MANAGER_ERROR_FIELDS);
      if (hidden.length > 0) {
        showToast(hidden.join('\n'), 'error');
      } else if (Object.keys(errorData).length === 0) {
        showToast(err?.response?.data?.message || 'Failed to update. Please try again.', 'error');
      }
    },
  });

  const updateClinicMutation = useMutation({
    mutationFn: updateClinic,
    onSuccess: () => {
      queryClient.invalidateQueries(['clinics']);
      setEditingSection(null);
      setErrors({});
      showToast('Facility details updated successfully!', 'success');
    },
    onError: (err) => {
      const errorData = err?.response?.data?.errors || {};
      setErrors(errorData);
      const hidden = collectHiddenErrors(errorData, FACILITY_ERROR_FIELDS);
      if (hidden.length > 0) {
        showToast(hidden.join('\n'), 'error');
      } else if (Object.keys(errorData).length === 0) {
        showToast(err?.response?.data?.message || 'Failed to update. Please try again.', 'error');
      }
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: submitChangePassword,
    onSuccess: () => {
      setPasswordData({ current: '', new: '', confirm: '' });
      showToast('Password changed successfully!', 'success');
    },
    onError: (err) => {
      showToast(err?.response?.data?.message || 'Failed to change password', 'error');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      navigation.replace('AuthScreen');
    },
    onError: (err) => {
      showToast(err?.response?.data?.message || 'Failed to delete account', 'error');
    },
  });

  const submitFeedbackMutation = useMutation({
    mutationFn: submitFeedback,
    onSuccess: () => {
      setFeedbackText('');
      setShowFeedback(false);
      showToast('Thank you for your feedback!', 'success');
    },
    onError: () => {
      showToast('Failed to submit feedback. Please try again.', 'error');
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      dispatch(resetFields());
      dispatch(setLoggedOut());
      navigation.replace('AuthScreen');
    },
    onError: () => {
      dispatch(resetFields());
      dispatch(setLoggedOut());
      navigation.replace('AuthScreen');
    },
  });

  // Toast notification (simple implementation)
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Profile image upload
  const {
    isUploading: isUploadingImage,
    triggerImagePicker,
  } = useProfileImageUpload({
    fieldName: 'clinic_profile_image',
    scope: 'clinic_owner',
    role: 'clinic',
    maxSizeMB: 5,
    onSuccess: () => {
      showToast('Facility picture updated successfully!', 'success');
    },
    onError: (error) => {
      showToast(error, 'error');
    },
  });

  // Initialize clinic data
  useEffect(() => {
    if (clinicsQuery?.data?.data) {
      const clinicFields = clinicsQuery.data.data;
      setClinicData(clinicFields[0]);
    }
  }, [clinicsQuery]);

  // Initialize form data when clinic loads
  useEffect(() => {
    if (clinicData && profileData) {
      setFormData({
        // Facility info
        name: clinicData?.name || '',
        type: clinicData?.type || '',
        address: clinicData?.address || '',
        city: clinicData?.city || '',
        state: clinicData?.state || '',
        postcode: clinicData?.postcode || '',
        phone_number: clinicData?.phone_number || '',
        email: profileData?.email || '',
        business_registration_number: clinicData?.business_registration_number || '',
        department: clinicData?.department || '',
        // Manager info
        first_name: profileData?.first_name || '',
        manager_phone: profileData?.phone_number || '',
        emergency_name: profileData?.emergency_name || '',
        emergency_number: profileData?.emergency_number || '',
      });
    }
  }, [clinicData, profileData]);

  // Initialize selected facility when modal opens
  useEffect(() => {
    if (showFacilityModal && profileData?.fac_id && facilityTypes) {
      // Find the currently selected facility in the facility types
      let matchedFacility = null;
      for (const facility of facilityTypes) {
        // Check if it's a top-level facility
        if (facility.fac_id === profileData.fac_id) {
          matchedFacility = facility;
          break;
        }
        // Check subtypes
        if (facility.types && facility.types.length > 0) {
          const childFacility = facility.types.find(
            (type) => type.fac_id === profileData.fac_id
          );
          if (childFacility) {
            matchedFacility = childFacility;
            // Expand the parent
            setExpandedFacilityTypes(prev =>
              prev.includes(facility.id) ? prev : [...prev, facility.id]
            );
            break;
          }
        }
      }
      if (matchedFacility) {
        setSelectedFacility(matchedFacility);
      } else {
        setSelectedFacility(null);
      }
    } else if (showFacilityModal) {
      setSelectedFacility(null);
    }
  }, [showFacilityModal, profileData?.fac_id, facilityTypes]);

  // Country-specific labels
  const getBusinessRegistrationLabel = (countryCode) => {
    const labels = {
      'MY': 'Business Registration No.',
      'SG': 'Unique Entity No.',
      'PH': 'Business Registration No.',
      'ID': 'Business Identification No.'
    };
    return labels[countryCode] || 'Business Registration No.';
  };

  const divisionLabel = getAdministrativeDivisionLabel(country);

  // Facility type name resolution
  const facilityTypeName = useMemo(() => {
    const facInfo = profileData?.fac;
    if (!facInfo) return clinicData?.type || 'Not specified';

    const countryFieldMap = {
      MY: 'name_malaysia',
      SG: 'name_singapore',
      PH: 'name_philippines',
      ID: 'name_indonesia',
    };

    const localeKey = countryFieldMap[country?.toUpperCase?.()] || countryFieldMap.MY;
    return (
      facInfo?.[localeKey] ??
      facInfo?.name_malaysia ??
      facInfo?.name_singapore ??
      clinicData?.type ??
      'Not specified'
    );
  }, [profileData, clinicData, country]);

  // Calculate profile completion
  const profileCompletion = useMemo(() => {
    let completed = 0;
    const total = 10;

    if (clinicData?.name) completed++;
    if (facilityTypeName && facilityTypeName !== 'Not specified') completed++;
    if (clinicData?.address) completed++;
    if (clinicData?.phone_number) completed++;
    if (profileData?.email) completed++;
    if (clinicData?.business_registration_number) completed++;
    if (profileData?.first_name) completed++;
    if (profileData?.phone_number) completed++;
    if (profileData?.emergency_name) completed++;
    if (profileData?.emergency_number) completed++;

    return Math.round((completed / total) * 100);
  }, [clinicData, profileData, facilityTypeName]);

  // Section refs for scrolling
  const sectionRefs = useRef({});

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    // In web, we can use scrollIntoView
    if (Platform.OS === 'web' && sectionRefs.current[sectionId]) {
      sectionRefs.current[sectionId].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Helper to get error message for a field (handles array of errors)
  const getFieldError = (fieldName) => {
    const fieldErrors = errors[fieldName];
    if (!fieldErrors) return null;
    if (Array.isArray(fieldErrors)) {
      return fieldErrors.join('\n');
    }
    return fieldErrors;
  };

  // Handle form changes
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Save handlers
  const handleSaveFacilityInfo = () => {
    if (!clinicData?.uuid) return;

    updateClinicMutation.mutate({
      uuid: clinicData.uuid,
      name: formData.name,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      postcode: formData.postcode,
      phone_number: formData.phone_number,
      business_registration_number: formData.business_registration_number,
      department: formData.department,
    });
  };

  const handleSaveManagerInfo = () => {
    // Note: last_name is intentionally not sent. This section has no input for it,
    // and the backend rejects it as `sometimes|required` — so an account with a
    // blank last_name could never save, with no field to surface the error on.
    updateProfileMutation.mutate({
      scope: 'clinic_owner',
      first_name: formData.first_name,
      phone_number: formData.manager_phone,
      emergency_name: formData.emergency_name,
      emergency_number: formData.emergency_number,
    });
  };

  // Facility specialization mutation
  const updateFacilityMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries(['profile']);
      queryClient.invalidateQueries(['clinics']);
      setShowFacilityModal(false);
      setFacilitySearchQuery('');
      setExpandedFacilityTypes([]);
      showToast('Facility specialization updated successfully!', 'success');
    },
    onError: (err) => {
      showToast(err?.response?.data?.message || 'Failed to update facility specialization', 'error');
    },
  });

  const handleSaveFacilitySpecialization = () => {
    if (!selectedFacility) {
      showToast('Please select a facility type', 'error');
      return;
    }

    updateFacilityMutation.mutate({
      fac_id: selectedFacility.fac_id,
      scope: 'clinic_owner',
    });
  };

  const handleCloseFacilityModal = () => {
    setShowFacilityModal(false);
    setFacilitySearchQuery('');
    setExpandedFacilityTypes([]);
  };

  const toggleFacilityTypeExpanded = (facilityId) => {
    setExpandedFacilityTypes(prev => {
      if (prev.includes(facilityId)) {
        return prev.filter(id => id !== facilityId);
      }
      return [...prev, facilityId];
    });
  };

  const handleChangePassword = () => {
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      showToast('Please fill in all password fields', 'error');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      showToast('New passwords do not match', 'error');
      return;
    }
    if (passwordData.new.length < 8) {
      showToast('Password must be at least 8 characters', 'error');
      return;
    }

    updatePasswordMutation.mutate({
      current_password: passwordData.current,
      password: passwordData.new,
      password_confirmation: passwordData.confirm,
    });
  };

  const handleDeleteAccount = () => {
    if (Platform.OS === 'web') {
      if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
        deleteUserMutation.mutate(slug);
      }
    } else {
      Alert.alert(
        'Delete Account',
        'Are you sure you want to delete your account? This action cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Delete', style: 'destructive', onPress: () => deleteUserMutation.mutate(slug) },
        ]
      );
    }
  };

  const handleSubmitFeedback = () => {
    if (!feedbackText.trim()) {
      showToast('Please enter your feedback', 'error');
      return;
    }
    submitFeedbackMutation.mutate({
      type: 'feedback',
      message: feedbackText,
    });
  };

  const handleLogout = () => {
    if (Platform.OS === 'web') {
      if (window.confirm('Are you sure you want to logout?')) {
        logoutMutation.mutate();
      }
    } else {
      Alert.alert(
        'Logout',
        'Are you sure you want to logout?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Logout', onPress: () => logoutMutation.mutate() },
        ]
      );
    }
  };

  const isLoading = profileLoading || clinicsLoading;

  if (isLoading) {
    return (
      <WebLayout>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={webColors.primary} />
          <Text style={styles.loadingText}>Loading profile...</Text>
        </View>
      </WebLayout>
    );
  }

  // Navigation items
  const navItems = [
    { id: 'facility-info', Icon: Building2, label: 'Facility Information' },
    { id: 'app-manager', Icon: User, label: 'App Manager Info' },
    { id: 'facility-specialization', Icon: Tag, label: 'Facility Specialization' },
    { id: 'change-password', Icon: Lock, label: 'Change Password' },
    { id: 'privacy-policy', Icon: Shield, label: 'Privacy Policy' },
    { id: 'help-support', Icon: HelpCircle, label: 'Help & Support' },
  ];

  // FAQ items
  const faqItems = [
    {
      question: 'How do I post a job on SuperMedic?',
      answer: 'Go to the Dashboard and click "Post a Job". Fill in the job details, select the profession type, and submit. Your job will be visible to qualified healthcare professionals immediately.',
    },
    {
      question: 'How do I review applicants?',
      answer: 'When healthcare professionals apply to your jobs, you\'ll receive notifications. Go to your job listing and click "View Candidates" to review applications, view profiles, and approve or reject applicants.',
    },
    {
      question: 'What documents do applicants need?',
      answer: 'Healthcare professionals must have verified medical licenses (APC, MMC), valid identification, and relevant certifications. You can view their verification status in their profile.',
    },
    {
      question: 'How do I contact an applicant?',
      answer: 'Once you\'ve approved an applicant, you can view their contact details including phone number. Communication should be professional and job-related.',
    },
    {
      question: 'What are the different job types?',
      answer: 'SuperMedic supports Locum (short-term), Full-Time (permanent), International, Temporary (fixed duration), and Contract positions. Each type has different posting options.',
    },
    {
      question: 'How do I manage my facility profile?',
      answer: 'Go to Account settings to update your facility information, app manager details, and specialization. Keep your profile complete to attract more qualified applicants.',
    },
  ];

  return (
    <WebLayout>
      {/* Toast Notification */}
      {toast && (
        <View style={[
          styles.toast,
          toast.type === 'success' && styles.toastSuccess,
          toast.type === 'error' && styles.toastError,
        ]}>
          <Text style={styles.toastText}>{toast.message}</Text>
        </View>
      )}

      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Page Header */}
        <View style={styles.pageHeader}>
          <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Account</Text>
          <Text style={styles.pageSubtitle}>Manage your facility and settings</Text>
        </View>

        {/* Two Column Layout */}
        <View style={[styles.accountLayout, isMobile && styles.accountLayoutMobile]}>
          {/* Sidebar */}
          <View style={[styles.sidebar, isMobile && styles.sidebarMobile]}>
            {/* Profile Card */}
            <View style={styles.profileCard}>
              <TouchableOpacity
                style={styles.profilePictureWrapper}
                onPress={triggerImagePicker}
                disabled={isUploadingImage}
              >
                {isUploadingImage ? (
                  <View style={styles.userAvatar}>
                    <ActivityIndicator size="small" color="#ffffff" />
                  </View>
                ) : (
                  <Image
                    source={{ uri: clinicData?.profile_image
                      ? 'data:image/jpg;base64,' + clinicData.profile_image
                      : PLACEHOLDER_PROFILE_URL }}
                    style={styles.userAvatarImage}
                  />
                )}
                <View style={[styles.profilePictureOverlay, isUploadingImage && styles.profilePictureOverlayDisabled]}>
                  <Pencil size={14} color="#ffffff" />
                </View>
              </TouchableOpacity>

              <Text style={styles.facilityName}>{clinicData?.name || 'Your Facility'}</Text>
              <Text style={styles.facilityRole}>Facility Manager</Text>

              <View style={styles.progressBar}>
                <View style={[styles.progressFill, { width: `${profileCompletion}%` }]} />
              </View>
              <Text style={styles.progressText}>Profile {profileCompletion}% Complete</Text>

              {profileCompletion < 100 && (
                <TouchableOpacity
                  style={styles.completeProfileBtn}
                  onPress={() => scrollToSection('facility-info')}
                >
                  <View style={styles.completeProfileBtnContent}>
                    <AlertTriangle size={16} color="#ffffff" />
                    <Text style={styles.completeProfileBtnText}>Complete Profile</Text>
                  </View>
                </TouchableOpacity>
              )}
            </View>

            {/* Navigation Menu (desktop only) */}
            {!isMobile && (
            <View style={styles.navMenu}>
              {navItems.map((item, index) => (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.navItem,
                    activeSection === item.id && styles.navItemActive,
                    index === navItems.length - 1 && styles.navItemLast,
                  ]}
                  onPress={() => scrollToSection(item.id)}
                >
                  <View style={styles.navItemIcon}>
                    <item.Icon
                      size={18}
                      color={activeSection === item.id ? webColors.primaryDark : webColors.textSecondary}
                    />
                  </View>
                  <Text style={[
                    styles.navItemLabel,
                    activeSection === item.id && styles.navItemLabelActive,
                  ]}>
                    {item.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            )}
          </View>

          {/* Content Area */}
          <View style={[styles.accountContent, isMobile && styles.accountContentMobile]}>
            <View style={styles.contentSection}>
              {/* Facility Information */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['facility-info'] = el}
              >
                <Text style={styles.formSectionTitle}>Facility Information</Text>

                <View style={styles.formGrid}>
                  <FormField
                    label="Facility Name"
                    value={formData.name}
                    onChange={(v) => updateField('name', v)}
                    editable={editingSection === 'facility-info'}
                    fullWidth
                    error={getFieldError('name')}
                  />
                  <FormField
                    label="Facility Department"
                    value={formData.department}
                    onChange={(v) => updateField('department', v)}
                    editable={editingSection === 'facility-info'}
                    fullWidth
                    placeholder="e.g., Emergency, Outpatient, etc."
                    error={getFieldError('department')}
                  />
                  <FormField
                    label="Facility Type"
                    value={facilityTypeName}
                    editable={false}
                    fullWidth
                    hint="Edit in Facility Specialization section"
                  />
                  <FormField
                    label="Facility Address"
                    value={formData.address}
                    onChange={(v) => updateField('address', v)}
                    editable={editingSection === 'facility-info'}
                    fullWidth
                    multiline
                    error={getFieldError('address')}
                  />
                  <FormField
                    label="City"
                    value={formData.city}
                    onChange={(v) => updateField('city', v)}
                    editable={editingSection === 'facility-info'}
                    error={getFieldError('city')}
                  />
                  <FormField
                    label={divisionLabel}
                    value={formData.state}
                    onChange={(v) => updateField('state', v)}
                    editable={editingSection === 'facility-info'}
                    error={getFieldError('state')}
                  />
                  <FormField
                    label="Postcode"
                    value={formData.postcode}
                    onChange={(v) => updateField('postcode', v)}
                    editable={editingSection === 'facility-info'}
                    error={getFieldError('postcode')}
                  />
                  <FormField
                    label="Facility Phone No."
                    value={formData.phone_number}
                    onChange={(v) => updateField('phone_number', v)}
                    editable={editingSection === 'facility-info'}
                    keyboardType="phone-pad"
                    error={getFieldError('phone_number')}
                  />
                  <FormField
                    label="Facility Email"
                    value={formData.email}
                    editable={false}
                    error={getFieldError('email')}
                  />
                  <FormField
                    label={getBusinessRegistrationLabel(country)}
                    value={formData.business_registration_number}
                    onChange={(v) => updateField('business_registration_number', v)}
                    editable={editingSection === 'facility-info'}
                    error={getFieldError('business_registration_number')}
                  />
                </View>

                {editingSection !== 'facility-info' ? (
                  <TouchableOpacity
                    style={styles.btnSecondary}
                    onPress={() => setEditingSection('facility-info')}
                  >
                    <Text style={styles.btnSecondaryText}>Edit Facility Information</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.sectionEditActions}>
                    <TouchableOpacity
                      style={styles.cancelBtn}
                      onPress={() => {
                        setEditingSection(null);
                        setErrors({});
                      }}
                    >
                      <Text style={styles.cancelBtnText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.saveBtn, updateClinicMutation.isPending && styles.saveBtnDisabled]}
                      onPress={handleSaveFacilityInfo}
                      disabled={updateClinicMutation.isPending}
                    >
                      <Text style={styles.saveBtnText}>
                        {updateClinicMutation.isPending ? 'Saving...' : 'Save'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* App Manager Information */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['app-manager'] = el}
              >
                <Text style={styles.formSectionTitle}>App Manager Information</Text>

                <View style={styles.formGrid}>
                  <FormField
                    label="App Manager Name"
                    value={formData.first_name}
                    onChange={(v) => updateField('first_name', v)}
                    editable={editingSection === 'app-manager'}
                    error={getFieldError('first_name')}
                  />
                  <FormField
                    label="App Manager Mobile No."
                    value={formData.manager_phone}
                    onChange={(v) => {
                      setFormData(prev => ({ ...prev, manager_phone: v }));
                      // Clear error for 'phone_number' (the API key) when user types
                      if (errors['phone_number']) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors['phone_number'];
                          return newErrors;
                        });
                      }
                    }}
                    editable={editingSection === 'app-manager'}
                    keyboardType="phone-pad"
                    error={getFieldError('phone_number')}
                  />
                  <FormField
                    label="App Assistant Manager Name"
                    value={formData.emergency_name}
                    onChange={(v) => updateField('emergency_name', v)}
                    editable={editingSection === 'app-manager'}
                    error={getFieldError('emergency_name')}
                  />
                  <FormField
                    label="App Assistant Manager Mobile No."
                    value={formData.emergency_number}
                    onChange={(v) => updateField('emergency_number', v)}
                    editable={editingSection === 'app-manager'}
                    keyboardType="phone-pad"
                    error={getFieldError('emergency_number')}
                  />
                </View>

                {editingSection !== 'app-manager' ? (
                  <TouchableOpacity
                    style={styles.btnSecondary}
                    onPress={() => setEditingSection('app-manager')}
                  >
                    <Text style={styles.btnSecondaryText}>Edit App Manager Information</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.sectionEditActions}>
                    <TouchableOpacity
                      style={styles.cancelBtn}
                      onPress={() => {
                        setEditingSection(null);
                        setErrors({});
                      }}
                    >
                      <Text style={styles.cancelBtnText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.saveBtn, updateProfileMutation.isPending && styles.saveBtnDisabled]}
                      onPress={handleSaveManagerInfo}
                      disabled={updateProfileMutation.isPending}
                    >
                      <Text style={styles.saveBtnText}>
                        {updateProfileMutation.isPending ? 'Saving...' : 'Save'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* Facility Specialization */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['facility-specialization'] = el}
              >
                <Text style={styles.formSectionTitle}>Facility Specialization</Text>

                {profileData?.role?.clinics?.[0]?.fac_list ? (
                  <FacilitySpecializationDisplay
                    facList={profileData.role.clinics[0].fac_list}
                    country={country}
                  />
                ) : (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyStateText}>No facility specialization selected</Text>
                  </View>
                )}

                <TouchableOpacity
                  style={styles.btnSecondary}
                  onPress={() => setShowFacilityModal(true)}
                >
                  <Text style={styles.btnSecondaryText}>Edit Facility Specialization</Text>
                </TouchableOpacity>
              </View>

              {/* Change Password */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['change-password'] = el}
              >
                <Text style={styles.formSectionTitle}>Change Password</Text>

                <View style={styles.formGrid}>
                  <View style={styles.formGroupFullWidth}>
                    <Text style={styles.formLabel}>Current Password</Text>
                    <TextInput
                      style={styles.formInput}
                      value={passwordData.current}
                      onChangeText={(v) => setPasswordData(prev => ({ ...prev, current: v }))}
                      secureTextEntry
                      placeholder="Enter current password"
                      placeholderTextColor={webColors.textMuted}
                    />
                  </View>
                  <View style={[styles.formGroup, isMobile && styles.formGroupMobile]}>
                    <Text style={styles.formLabel}>New Password</Text>
                    <TextInput
                      style={styles.formInput}
                      value={passwordData.new}
                      onChangeText={(v) => setPasswordData(prev => ({ ...prev, new: v }))}
                      secureTextEntry
                      placeholder="Enter new password"
                      placeholderTextColor={webColors.textMuted}
                    />
                  </View>
                  <View style={[styles.formGroup, isMobile && styles.formGroupMobile]}>
                    <Text style={styles.formLabel}>Confirm New Password</Text>
                    <TextInput
                      style={styles.formInput}
                      value={passwordData.confirm}
                      onChangeText={(v) => setPasswordData(prev => ({ ...prev, confirm: v }))}
                      secureTextEntry
                      placeholder="Confirm new password"
                      placeholderTextColor={webColors.textMuted}
                    />
                  </View>
                </View>

                <TouchableOpacity
                  style={[styles.btnSecondary, updatePasswordMutation.isPending && styles.btnDisabled]}
                  onPress={handleChangePassword}
                  disabled={updatePasswordMutation.isPending}
                >
                  <Text style={styles.btnSecondaryText}>
                    {updatePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Privacy Policy */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['privacy-policy'] = el}
              >
                <Text style={styles.formSectionTitle}>Privacy Policy</Text>

                <View style={styles.privacyBox}>
                  <Text style={styles.privacyText}>
                    SuperMedic is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your personal information.
                  </Text>
                  <Text style={styles.privacyText}>
                    <Text style={styles.privacyBold}>Information We Collect:</Text> We collect information you provide when creating an account, posting jobs, and using our services.
                  </Text>
                  <Text style={styles.privacyText}>
                    <Text style={styles.privacyBold}>How We Use Your Information:</Text> Your data is used to connect you with healthcare professionals, improve our services, and communicate important updates.
                  </Text>
                </View>

                <TouchableOpacity onPress={() => {}} style={styles.linkRow}>
                  <Text style={styles.linkText}>Read Full Privacy Policy</Text>
                  <ArrowRight size={14} color={webColors.primary} />
                </TouchableOpacity>
              </View>

              {/* Help & Support */}
              <View
                style={styles.formSection}
                ref={el => sectionRefs.current['help-support'] = el}
              >
                <Text style={styles.formSectionTitle}>Help & Support</Text>

                {!showFAQ && !showFeedback ? (
                  <View style={styles.helpOptions}>
                    <TouchableOpacity
                      style={styles.helpOptionRow}
                      onPress={() => setShowFAQ(true)}
                    >
                      <View style={styles.helpOptionIcon}>
                        <BookOpen size={24} color={webColors.primary} />
                      </View>
                      <View style={styles.helpOptionInfo}>
                        <Text style={styles.helpOptionTitle}>FAQs</Text>
                        <Text style={styles.helpOptionDesc}>Find answers to common questions</Text>
                      </View>
                      <ArrowRight size={16} color={webColors.textMuted} />
                    </TouchableOpacity>

                    <TouchableOpacity style={styles.helpOptionRow}>
                      <View style={styles.helpOptionIcon}>
                        <Mail size={24} color={webColors.primary} />
                      </View>
                      <View style={styles.helpOptionInfo}>
                        <Text style={styles.helpOptionTitle}>Email Support</Text>
                        <Text style={styles.helpOptionDesc}>support@supermedic.health</Text>
                      </View>
                      <ArrowRight size={16} color={webColors.textMuted} />
                    </TouchableOpacity>

                    <TouchableOpacity style={styles.helpOptionRow}>
                      <View style={styles.helpOptionIcon}>
                        <GraduationCap size={24} color={webColors.primary} />
                      </View>
                      <View style={styles.helpOptionInfo}>
                        <Text style={styles.helpOptionTitle}>Learn How to Use SuperMedic</Text>
                        <Text style={styles.helpOptionDesc}>Watch tutorials on YouTube</Text>
                      </View>
                      <ArrowRight size={16} color={webColors.textMuted} />
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.helpOptionRow}
                      onPress={() => setShowFeedback(true)}
                    >
                      <View style={styles.helpOptionIcon}>
                        <MessageSquare size={24} color={webColors.primary} />
                      </View>
                      <View style={styles.helpOptionInfo}>
                        <Text style={styles.helpOptionTitle}>Give Us Feedback</Text>
                        <Text style={styles.helpOptionDesc}>Share your thoughts and suggestions</Text>
                      </View>
                      <ArrowRight size={16} color={webColors.textMuted} />
                    </TouchableOpacity>
                  </View>
                ) : showFAQ ? (
                  <View>
                    <TouchableOpacity
                      style={styles.backButton}
                      onPress={() => setShowFAQ(false)}
                    >
                      <View style={styles.backButtonContent}>
                        <ArrowLeft size={14} color={webColors.textSecondary} />
                        <Text style={styles.backButtonText}>Back to Help & Support</Text>
                      </View>
                    </TouchableOpacity>

                    <View style={styles.faqList}>
                      {faqItems.map((item, index) => (
                        <TouchableOpacity
                          key={index}
                          style={[
                            styles.faqItem,
                            expandedFAQ === index && styles.faqItemOpen,
                          ]}
                          onPress={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                        >
                          <View style={styles.faqQuestion}>
                            <Text style={[
                              styles.faqQuestionText,
                              expandedFAQ === index && styles.faqQuestionTextOpen,
                            ]}>
                              {item.question}
                            </Text>
                            <View style={[
                              styles.faqArrow,
                              expandedFAQ === index && styles.faqArrowOpen,
                            ]}>
                              <ChevronDown
                                size={16}
                                color={expandedFAQ === index ? webColors.primary : webColors.textMuted}
                              />
                            </View>
                          </View>
                          {expandedFAQ === index && (
                            <Text style={styles.faqAnswer}>{item.answer}</Text>
                          )}
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                ) : (
                  <View>
                    <TouchableOpacity
                      style={styles.backButton}
                      onPress={() => setShowFeedback(false)}
                    >
                      <View style={styles.backButtonContent}>
                        <ArrowLeft size={14} color={webColors.textSecondary} />
                        <Text style={styles.backButtonText}>Back to Help & Support</Text>
                      </View>
                    </TouchableOpacity>

                    <View style={styles.feedbackForm}>
                      <Text style={styles.formLabel}>Your Feedback</Text>
                      <TextInput
                        style={styles.feedbackTextarea}
                        value={feedbackText}
                        onChangeText={setFeedbackText}
                        placeholder="Tell us what you think about SuperMedic. Your feedback helps us improve!"
                        placeholderTextColor={webColors.textMuted}
                        multiline
                        numberOfLines={6}
                      />
                      <TouchableOpacity
                        style={[styles.btnPrimary, submitFeedbackMutation.isPending && styles.btnDisabled]}
                        onPress={handleSubmitFeedback}
                        disabled={submitFeedbackMutation.isPending}
                      >
                        <Text style={styles.btnPrimaryText}>
                          {submitFeedbackMutation.isPending ? 'Submitting...' : 'Submit Feedback'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </View>

              {/* Delete Account */}
              <View style={styles.deleteSection}>
                <TouchableOpacity
                  style={styles.btnDelete}
                  onPress={handleDeleteAccount}
                >
                  <Text style={styles.btnDeleteText}>Delete Account</Text>
                </TouchableOpacity>
              </View>

              {/* Logout */}
              <View style={styles.logoutSection}>
                <TouchableOpacity
                  style={[styles.btnLogout, logoutMutation.isPending && styles.btnDisabled]}
                  onPress={handleLogout}
                  disabled={logoutMutation.isPending}
                >
                  <Text style={styles.btnLogoutText}>
                    {logoutMutation.isPending ? 'Logging out...' : 'Logout'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>

        <View style={{ height: 60 }} />
      </ScrollView>

      {/* Facility Specialization Modal */}
      {showFacilityModal && (
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          {/* Modal Header */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Edit Facility Specialization</Text>
            <TouchableOpacity
              onPress={handleCloseFacilityModal}
              style={styles.modalCloseBtn}
            >
              <X size={20} color={webColors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Search Input */}
          <View style={styles.modalSearchWrapper}>
            <View style={styles.modalSearchIcon}>
              <Search size={16} color={webColors.textMuted} />
            </View>
            <TextInput
              style={styles.modalSearchInput}
              placeholder="Search facility type..."
              placeholderTextColor={webColors.textMuted}
              value={facilitySearchQuery}
              onChangeText={(text) => {
                setFacilitySearchQuery(text);
                // Auto-expand when searching
                if (text.length > 0 && facilityTypes) {
                  setExpandedFacilityTypes(facilityTypes.map(f => f.id));
                } else {
                  setExpandedFacilityTypes([]);
                }
              }}
            />
          </View>

          {/* Facility Types List */}
          <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true}>
            {facilityTypesLoading ? (
              <View style={styles.modalLoadingContainer}>
                <ActivityIndicator size="small" color={webColors.primary} />
                <Text style={styles.modalLoadingText}>Loading facility types...</Text>
              </View>
            ) : facilityTypesError ? (
              <View style={styles.modalEmptyState}>
                <Text style={styles.modalEmptyStateText}>Failed to load facility types</Text>
                <TouchableOpacity
                  style={styles.modalRetryBtn}
                  onPress={() => refetchFacilityTypes()}
                >
                  <Text style={styles.modalRetryBtnText}>Retry</Text>
                </TouchableOpacity>
              </View>
            ) : (
              (facilityTypes || [])
                .filter(facility => {
                  if (!facilitySearchQuery) return true;
                  const lowerSearch = facilitySearchQuery.toLowerCase();
                  const matchesName = facility.name.toLowerCase().includes(lowerSearch);
                  const hasMatchingType = facility.types?.some(type =>
                    type.name.toLowerCase().includes(lowerSearch)
                  );
                  return matchesName || hasMatchingType;
                })
                .map((facility) => {
                  const isExpanded = expandedFacilityTypes.includes(facility.id) || facilitySearchQuery.length > 0;
                  const hasSubtypes = facility.types && facility.types.length > 0;
                  const { title, subtitle } = extractFacilityTitleAndSubtitle(facility.name);
                  const isParentSelected = !hasSubtypes && selectedFacility?.id === facility.id;

                  return (
                    <View key={facility.id} style={styles.modalFacilityItem}>
                      {/* Parent Facility */}
                      <TouchableOpacity
                        style={[
                          styles.modalFacilityCard,
                          isExpanded && styles.modalFacilityCardExpanded,
                          isParentSelected && styles.modalFacilityCardSelected,
                        ]}
                        onPress={() => {
                          if (hasSubtypes) {
                            toggleFacilityTypeExpanded(facility.id);
                          } else {
                            setSelectedFacility(facility);
                          }
                        }}
                      >
                        <View style={[
                          styles.modalFacilityIconWrapper,
                          (isExpanded || isParentSelected) && styles.modalFacilityIconWrapperActive,
                        ]}>
                          <Building2 size={20} color={(isExpanded || isParentSelected) ? '#ffffff' : webColors.primary} />
                        </View>
                        <View style={styles.modalFacilityInfo}>
                          <Text style={[
                            styles.modalFacilityName,
                            (isExpanded || isParentSelected) && styles.modalFacilityNameActive,
                          ]}>
                            {title}
                          </Text>
                          {subtitle ? (
                            <Text style={styles.modalFacilitySubtitle}>{subtitle}</Text>
                          ) : null}
                          {hasSubtypes ? (
                            <Text style={styles.modalFacilityCount}>
                              {facility.types.length} type{facility.types.length !== 1 ? 's' : ''}
                            </Text>
                          ) : null}
                        </View>
                        {hasSubtypes ? (
                          <View style={isExpanded ? { transform: [{ rotate: '90deg' }] } : undefined}>
                            <ChevronRight
                              size={18}
                              color={isExpanded ? webColors.primary : webColors.textMuted}
                            />
                          </View>
                        ) : isParentSelected ? (
                          <Check size={18} color={webColors.primary} />
                        ) : null}
                      </TouchableOpacity>

                      {/* Subtypes */}
                      {hasSubtypes && isExpanded ? (
                        <View style={styles.modalSubtypesList}>
                          {facility.types.map((subtype) => {
                            const isSubtypeSelected = selectedFacility?.id === subtype.id;
                            const { title: subtypeTitle, subtitle: subtypeSubtitle } = extractFacilityTitleAndSubtitle(subtype.name);

                            return (
                              <TouchableOpacity
                                key={subtype.id}
                                style={[
                                  styles.modalSubtypeCard,
                                  isSubtypeSelected && styles.modalSubtypeCardSelected,
                                ]}
                                onPress={() => setSelectedFacility(subtype)}
                              >
                                <View style={[
                                  styles.modalRadioCircle,
                                  isSubtypeSelected && styles.modalRadioCircleSelected,
                                ]}>
                                  {isSubtypeSelected ? <Check size={12} color="#ffffff" /> : null}
                                </View>
                                <View style={styles.modalSubtypeInfo}>
                                  <Text style={[
                                    styles.modalSubtypeName,
                                    isSubtypeSelected && styles.modalSubtypeNameSelected,
                                  ]}>
                                    {subtypeTitle}
                                  </Text>
                                  {subtypeSubtitle ? (
                                    <Text style={styles.modalSubtypeSubtitle}>{subtypeSubtitle}</Text>
                                  ) : null}
                                </View>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                      ) : null}
                    </View>
                  );
                })
            )}
            {(() => {
              if (facilityTypesLoading || facilityTypesError) return null;
              const filteredCount = (facilityTypes || []).filter(facility => {
                if (!facilitySearchQuery) return true;
                const lowerSearch = facilitySearchQuery.toLowerCase();
                const matchesName = facility.name.toLowerCase().includes(lowerSearch);
                const hasMatchingType = facility.types?.some(type =>
                  type.name.toLowerCase().includes(lowerSearch)
                );
                return matchesName || hasMatchingType;
              }).length;
              if (filteredCount === 0) {
                return (
                  <View style={styles.modalEmptyState}>
                    <Text style={styles.modalEmptyStateText}>
                      {facilitySearchQuery ? `No facility types found matching "${facilitySearchQuery}"` : 'No facility types available'}
                    </Text>
                  </View>
                );
              }
              return null;
            })()}
          </ScrollView>

          {/* Modal Actions */}
          <View style={styles.modalActions}>
            <TouchableOpacity
              style={styles.modalCancelBtn}
              onPress={handleCloseFacilityModal}
            >
              <Text style={styles.modalCancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.modalSaveBtn,
                (!selectedFacility || updateFacilityMutation.isPending || facilityTypesLoading) &&
                styles.modalSaveBtnDisabled,
              ]}
              onPress={handleSaveFacilitySpecialization}
              disabled={!selectedFacility || updateFacilityMutation.isPending || facilityTypesLoading}
            >
              <Text style={styles.modalSaveBtnText}>
                {updateFacilityMutation.isPending ? 'Saving...' :
                 facilityTypesLoading ? 'Loading...' :
                 'Save'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    )}
    </WebLayout>
  );
};

// Form Field Component
const FormField = ({
  label,
  value,
  onChange,
  editable = true,
  multiline = false,
  fullWidth = false,
  placeholder = '',
  keyboardType = 'default',
  required = false,
  hint = '',
  error = null,
}) => {
  const isMobile = useIsMobile();
  return (
  <View style={[styles.formGroup, fullWidth && styles.formGroupFullWidth, isMobile && styles.formGroupMobile]}>
    <Text style={styles.formLabel}>
      {label}
      {required && <Text style={styles.requiredStar}> *</Text>}
    </Text>
    {editable ? (
      <TextInput
        style={[
          styles.formInput,
          multiline && styles.formInputMultiline,
          !editable && styles.formInputReadonly,
          error && styles.formInputError,
        ]}
        value={value}
        onChangeText={onChange}
        placeholder={placeholder || `Enter ${label.toLowerCase()}`}
        placeholderTextColor={webColors.textMuted}
        multiline={multiline}
        numberOfLines={multiline ? 3 : 1}
        keyboardType={keyboardType}
        editable={editable}
      />
    ) : (
      <View style={styles.formValueContainer}>
        <Text style={styles.formValue}>{value || '-'}</Text>
      </View>
    )}
    {hint && <Text style={styles.formHint}>{hint}</Text>}
    {error && <Text style={styles.fieldError}>{error}</Text>}
  </View>
  );
};

// Facility Specialization Display Component
const FacilitySpecializationDisplay = ({ facList, country }) => {
  const facType = facList.fac_type;

  const getCountrySpecificName = () => {
    switch (country) {
      case 'ID':
        return {
          specialization: facList.name_indonesia,
          type: facType?.name_indonesia,
          countryLabel: 'Indonesia'
        };
      case 'SG':
        return {
          specialization: facList.name_singapore,
          type: facType?.name_singapore,
          countryLabel: 'Singapore'
        };
      case 'PH':
        return {
          specialization: facList.name_philippines,
          type: facType?.name_philippines,
          countryLabel: 'Philippines'
        };
      case 'MY':
      default:
        return {
          specialization: facList.name_malaysia,
          type: facType?.name_malaysia,
          // Countries without their own facility names share the Malaysia list
          countryLabel: getCountryData(country).name
        };
    }
  };

  const countryData = getCountrySpecificName();

  return (
    <View style={styles.specializationDisplay}>
      <Text style={styles.specializationName}>{countryData.specialization}</Text>
      {countryData.type && (
        <View style={styles.specializationRow}>
          <View style={styles.specializationIcon}>
            <Building2 size={12} color={webColors.textSecondary} />
          </View>
          <Text style={styles.specializationLabel}>Type: {countryData.type}</Text>
        </View>
      )}
      <View style={styles.specializationRow}>
        <View style={styles.specializationIcon}>
          <Globe size={12} color={webColors.textMuted} />
        </View>
        <Text style={styles.specializationLabelSmall}>{countryData.countryLabel} Classification</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 100,
  },
  loadingText: {
    fontSize: 16,
    color: webColors.textMuted,
    marginTop: 12,
  },

  // Toast
  toast: {
    position: 'absolute',
    top: 80,
    right: 20,
    backgroundColor: webColors.cardBackground,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    zIndex: 1000,
    minWidth: 300,
    borderLeftWidth: 4,
    borderLeftColor: webColors.info,
  },
  toastSuccess: {
    borderLeftColor: webColors.success,
  },
  toastError: {
    borderLeftColor: webColors.error,
  },
  toastText: {
    fontSize: 14,
    color: webColors.textPrimary,
  },

  // Page Header
  pageHeader: {
    marginBottom: 32,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textSecondary,
  },

  // Layout
  accountLayout: {
    flexDirection: 'row',
    gap: 24,
  },

  // Sidebar
  sidebar: {
    width: 280,
    position: Platform.OS === 'web' ? 'sticky' : 'relative',
    top: 100,
    alignSelf: 'flex-start',
  },
  profileCard: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    marginBottom: 16,
  },
  profilePictureWrapper: {
    position: 'relative',
    width: 80,
    height: 80,
    marginBottom: 12,
  },
  userAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: webColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  userAvatarImage: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  userAvatarText: {
    fontSize: 28,
    fontWeight: '700',
    color: '#ffffff',
  },
  profilePictureOverlay: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    backgroundColor: webColors.primary,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#ffffff',
  },
  profilePictureOverlayDisabled: {
    opacity: 0.5,
  },
  editIcon: {
    fontSize: 14,
    color: '#ffffff',
  },
  facilityName: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
    textAlign: 'center',
  },
  facilityRole: {
    fontSize: 14,
    color: webColors.textMuted,
    marginBottom: 12,
  },
  progressBar: {
    width: '100%',
    height: 8,
    backgroundColor: webColors.border,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: webColors.primary,
    borderRadius: 4,
  },
  progressText: {
    fontSize: 13,
    color: webColors.textMuted,
    marginBottom: 12,
  },
  completeProfileBtn: {
    width: '100%',
    backgroundColor: webColors.primary,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    ...webShadows.button,
  },
  completeProfileBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
  completeProfileBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },

  // Navigation Menu
  navMenu: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    overflow: 'hidden',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
    borderLeftWidth: 3,
    borderLeftColor: 'transparent',
  },
  navItemLast: {
    borderBottomWidth: 0,
  },
  navItemActive: {
    backgroundColor: webColors.primaryLight,
    borderLeftColor: webColors.primary,
  },
  navItemIcon: {
    fontSize: 18,
  },
  navItemLabel: {
    fontSize: 15,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  navItemLabelActive: {
    color: webColors.primaryDark,
    fontWeight: '600',
  },

  // Content Area
  accountContent: {
    flex: 1,
  },
  contentSection: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    padding: 24,
  },

  // Form Sections
  formSection: {
    marginBottom: 32,
  },
  formSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
  },
  formSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  editSectionBtn: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 6,
  },
  editSectionBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.primary,
  },
  editActions: {
    flexDirection: 'row',
    gap: 8,
  },
  sectionEditActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  cancelBtn: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 6,
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  saveBtn: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    backgroundColor: webColors.success,
    borderRadius: 6,
  },
  saveBtnDisabled: {
    opacity: 0.6,
  },
  saveBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Form Grid
  formGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  formGroup: {
    width: Platform.OS === 'web' ? 'calc(50% - 10px)' : '48%',
  },
  formGroupFullWidth: {
    width: '100%',
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
    marginBottom: 8,
  },
  requiredStar: {
    color: webColors.error,
  },
  formInput: {
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontSize: 15,
    color: webColors.textPrimary,
    backgroundColor: webColors.cardBackground,
  },
  formInputMultiline: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  formInputReadonly: {
    backgroundColor: webColors.backgroundAlt,
  },
  formValueContainer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
  },
  formValue: {
    fontSize: 15,
    color: webColors.textPrimary,
  },
  formHint: {
    fontSize: 13,
    color: webColors.textMuted,
    marginTop: 4,
  },
  formInputError: {
    borderColor: webColors.error,
  },
  fieldError: {
    color: webColors.error,
    fontSize: 12,
    marginTop: 4,
  },

  // Empty State
  emptyState: {
    paddingVertical: 24,
    paddingHorizontal: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    marginBottom: 16,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    color: webColors.textMuted,
  },

  // Specialization Display
  specializationDisplay: {
    marginBottom: 16,
  },
  specializationName: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  specializationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  specializationIcon: {
    fontSize: 12,
    marginRight: 8,
  },
  specializationLabel: {
    fontSize: 14,
    color: webColors.textSecondary,
  },
  specializationLabelSmall: {
    fontSize: 12,
    color: webColors.textMuted,
  },

  // Privacy Policy
  privacyBox: {
    padding: 20,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
    marginBottom: 16,
    maxHeight: 200,
  },
  privacyText: {
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 22,
    marginBottom: 12,
  },
  privacyBold: {
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  linkText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.primary,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },

  // Help & Support
  helpOptions: {
    gap: 12,
  },
  helpOptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
  },
  helpOptionIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpOptionInfo: {
    flex: 1,
  },
  helpOptionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  helpOptionDesc: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  helpOptionArrow: {
    fontSize: 16,
    color: webColors.textMuted,
  },
  backButton: {
    marginBottom: 20,
  },
  backButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },

  // FAQ
  faqList: {
    gap: 12,
  },
  faqItem: {
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 10,
    overflow: 'hidden',
  },
  faqItemOpen: {
    backgroundColor: webColors.primaryLight,
  },
  faqQuestion: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    paddingHorizontal: 20,
  },
  faqQuestionText: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  faqQuestionTextOpen: {
    color: webColors.primaryDark,
  },
  faqArrow: {
    marginLeft: 12,
  },
  faqArrowOpen: {
    transform: [{ rotate: '180deg' }],
  },
  faqAnswer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 22,
  },

  // Feedback Form
  feedbackForm: {
    marginTop: 12,
  },
  feedbackTextarea: {
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontSize: 14,
    color: webColors.textPrimary,
    minHeight: 150,
    textAlignVertical: 'top',
    marginBottom: 16,
  },

  // Buttons
  btnSecondary: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.primary,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginTop: 16,
  },
  btnSecondaryText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.primary,
  },
  btnPrimary: {
    backgroundColor: webColors.primary,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    ...webShadows.button,
    alignSelf: 'flex-start',
  },
  btnPrimaryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  btnDisabled: {
    opacity: 0.6,
  },

  // Delete Section
  deleteSection: {
    borderTopWidth: 1,
    borderTopColor: webColors.border,
    paddingTop: 32,
  },
  btnDelete: {
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: webColors.error,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  btnDeleteText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.error,
  },

  // Logout Section
  logoutSection: {
    marginTop: 24,
  },
  btnLogout: {
    backgroundColor: webColors.textSecondary,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  btnLogoutText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Facility Modal Styles
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    paddingHorizontal: 20,
  },
  modalContent: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    width: '100%',
    maxWidth: 520,
    maxHeight: '85vh',
    overflow: 'hidden',
    ...webShadows.card,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  modalCloseBtn: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: webColors.backgroundAlt,
  },
  modalSearchWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 20,
    paddingHorizontal: 14,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: webColors.border,
  },
  modalSearchIcon: {
    marginRight: 10,
  },
  modalSearchInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 14,
    color: webColors.textPrimary,
    outlineStyle: 'none',
  },
  modalScrollView: {
    maxHeight: 400,
    paddingHorizontal: 20,
  },
  modalLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    gap: 12,
  },
  modalLoadingText: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  modalFacilityItem: {
    marginBottom: 12,
  },
  modalFacilityCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  modalFacilityCardExpanded: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  modalFacilityCardSelected: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  modalFacilityIconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  modalFacilityIconWrapperActive: {
    backgroundColor: webColors.primary,
  },
  modalFacilityInfo: {
    flex: 1,
  },
  modalFacilityName: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  modalFacilityNameActive: {
    color: webColors.primaryDark,
  },
  modalFacilitySubtitle: {
    fontSize: 13,
    color: webColors.textMuted,
    marginTop: 2,
  },
  modalFacilityCount: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 4,
  },
  modalSubtypesList: {
    marginLeft: 58,
    marginTop: 8,
    gap: 8,
  },
  modalSubtypeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    backgroundColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: webColors.border,
  },
  modalSubtypeCardSelected: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  modalRadioCircle: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: webColors.border,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalRadioCircleSelected: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primary,
  },
  modalSubtypeInfo: {
    flex: 1,
  },
  modalSubtypeName: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textPrimary,
  },
  modalSubtypeNameSelected: {
    color: webColors.primaryDark,
    fontWeight: '600',
  },
  modalSubtypeSubtitle: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 2,
  },
  modalEmptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  modalEmptyStateText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
  },
  modalRetryBtn: {
    marginTop: 12,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: webColors.primary,
    borderRadius: 6,
  },
  modalRetryBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: webColors.border,
  },
  modalCancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 8,
  },
  modalCancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  modalSaveBtn: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: webColors.primary,
    borderRadius: 8,
    ...webShadows.button,
  },
  modalSaveBtnDisabled: {
    opacity: 0.5,
  },
  modalSaveBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },

  // ==================== MOBILE VARIANTS ====================
  accountLayoutMobile: {
    flexDirection: 'column',
  },
  sidebarMobile: {
    width: '100%',
    position: 'relative',
    top: 0,
  },
  accountContentMobile: {
    flex: 0,
    width: '100%',
  },
  formGroupMobile: {
    width: '100%',
  },
  pageTitleMobile: {
    fontSize: 22,
  },
});

export default ProfileFacilitiesScreen;
